import SubCategory from 'components/Category/components/SubCategory';
import { MenuItem } from 'generated/graphql';
import useOnPathChange from 'hooks/onPathChange';
import useMenuSlugParser from 'hooks/useMenuSlugParser';
import { useState, useCallback, useEffect } from 'react';
import { Box } from '@mui/material';
import { colors } from 'styles/theme/colors';
import { CenteredItemLink } from '../CenteredItemLink';
import { LevelTwoAnimationWrapper } from '../LevelTwoAnimationWrapper';
import { LevelThreeCategory } from './components/LevelThreeCategory';

type LevelTwoCategoryProps = {
    category: MenuItem;
    isFocused: boolean;
};

export function LevelTwoCategory({ category, isFocused }: LevelTwoCategoryProps) {
    const { parseSlug } = useMenuSlugParser();
    const [showingSubCategory, setShowingSubCategory] = useState<MenuItem>();
    const closeSubMenu = useCallback(() => setShowingSubCategory(undefined), []);
    const [focusLastRender, setFocusLastRender] = useState(false);

    useOnPathChange(closeSubMenu);
    useEffect(() => {
        if (!isFocused) {
            setShowingSubCategory(undefined);
        }
        setFocusLastRender(isFocused);
    }, [focusLastRender, isFocused]);

    return (
        <LevelTwoAnimationWrapper
            showLevelThree={Boolean(showingSubCategory)}
            levelThreeChildren={(
                <LevelThreeCategory
                    closeSubMenu={closeSubMenu}
                    showingSubCategory={showingSubCategory!}
                />
            )}
            levelTwoChildren={(
                <>
                    {category.items.map((subCategory, subCategoryIndex) => {
                        const isParent = subCategory.items.length > 0;

                        if (!isParent && !subCategory.image) {
                            return (
                                <CenteredItemLink
                                    key={subCategoryIndex}
                                    item={subCategory}
                                    typographyProps={{
                                        fontWeight: 600,
                                        variant: 'h2',
                                        whiteSpace: 'pre-wrap',
                                        textAlign: 'center',
                                    }}
                                />
                            );
                        }

                        return (
                            <Box
                                sx={{ display: 'flex', justifyContent: 'center' }}
                                key={subCategoryIndex}
                            >
                                <SubCategory
                                    height={100}
                                    menuItem={subCategory}
                                    key={subCategoryIndex}
                                    buttonProps={{
                                        href: isParent ? undefined : parseSlug(subCategory),
                                        onClick: isParent ? () => setShowingSubCategory(subCategory) : undefined,
                                    }}
                                    typographyProps={{
                                        variant: 'h3',
                                        sx: {
                                            textAlign: 'center',
                                            fontWeight: 600,
                                            marginTop: '10px',
                                        },
                                    }}
                                />
                            </Box>
                        );
                    })}
                </>
            )}
        />
    );
}
